/**
 * «Agregar algo que falta» (OPUS ci, idea 7): un adicional que se le olvidó a quien cotiza, sea en una OPCIÓN
 * antes de usarla o en un principal que ya está en la hoja. Se busca en los catálogos (gratis) o se describe
 * para generarlo con IA; la cantidad es la MISMA medición del principal si el adicional se cobra en la misma
 * unidad, o la que se escriba. Nada de factores fijos ni conversiones de unidad en código.
 *
 * Devuelve un `RenglonDeOpcion`; quien lo abre decide a dónde va (la opción o la hoja).
 */
import { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Input, Spinner, Tab, TabList } from '@fluentui/react-components';
import { Search16Regular } from '@fluentui/react-icons';
import { fotoAMano, mismaMedicion, type FotoGenerador, type RenglonDeOpcion } from '@supernova/shared';
import type { ConceptoParaOpcion } from './OpcionesDelConcepto.js';

export interface PrincipalParaAdicional {
  descripcion: string;
  unidad: string | null;
  cantidad: number;
  cantidadMedida: number | null;
  generador: FotoGenerador | null;
}

export interface AgregarAdicionalProps {
  principal: PrincipalParaAdicional;
  buscarTexto: (q: string) => Promise<ConceptoParaOpcion[]>;
  /** «Recomendados»: lo que el catálogo recomienda para lo que falta (cuesta tokens la primera vez: se pide al pulsar). Sin esto no se ofrece la pestaña. */
  recomendar?: (frase: string) => Promise<ConceptoParaOpcion[]>;
  formatoPrecio: (n: number) => string;
  /** Mete el adicional donde corresponda; si falla, lanza con un mensaje legible. */
  onAgregar: (r: RenglonDeOpcion) => Promise<void>;
  onCerrar: () => void;
}

const unidadBonita = (u: string | null | undefined) => (u ?? '').replace(/m2$/i, 'm²').replace(/m3$/i, 'm³');
// El texto del concepto se muestra completo: es lo que la persona necesita leer para decidir.
const corto = (s: string, _n?: number) => s;

/** Arma el renglón del adicional con la cantidad que corresponde (misma medición del principal o la escrita). */
export function renglonDeAdicional(args: {
  principal: PrincipalParaAdicional;
  concepto: { origen: 'catalogo' | 'ia'; catalogId: string | null; clave: string | null; descripcion: string; unidad: string | null; precio: number | null };
  cantidadTexto: string;
}): { ok: true; renglon: RenglonDeOpcion; mismaMedicion: boolean } | { ok: false; motivo: string } {
  const { principal, concepto } = args;
  const misma = principal.generador && principal.cantidad > 0 ? mismaMedicion(principal, concepto.unidad) : null;
  const escrita = Number(args.cantidadTexto.replace(',', '.'));
  const base = {
    rol: 'complemento' as const,
    origen: concepto.origen,
    catalogId: concepto.catalogId,
    clave: concepto.clave,
    descripcion: concepto.descripcion,
    unidad: concepto.unidad,
    precioUnitario: concepto.precio,
    revisar: true,
    aMano: true,
  };
  // Si el usuario escribió una cantidad, manda esa; si no y se cobra igual que el principal, su misma medición.
  if (!(escrita > 0) && misma && misma.generador) {
    const foto: FotoGenerador = { ...misma.generador, origen: 'complementario', regla: 'La misma medición que el concepto principal' };
    return {
      ok: true,
      mismaMedicion: true,
      renglon: {
        ...base,
        cantidad: misma.cantidad,
        cantidadMedida: misma.cantidadMedida,
        importe: concepto.precio != null ? Math.round((concepto.precio * misma.cantidad + Number.EPSILON) * 100) / 100 : null,
        generador: foto,
        // Con líneas por elemento sí se puede juntar y separar exacto: no es «a mano».
        aMano: false,
      },
    };
  }
  if (!(escrita > 0)) return { ok: false, motivo: 'Escribe la cantidad.' };
  return {
    ok: true,
    mismaMedicion: false,
    renglon: {
      ...base,
      cantidad: escrita,
      cantidadMedida: null,
      importe: concepto.precio != null ? Math.round((concepto.precio * escrita + Number.EPSILON) * 100) / 100 : null,
      generador: fotoAMano(escrita, concepto.unidad),
    },
  };
}

export function AgregarAdicional(p: AgregarAdicionalProps) {
  const [pestana, setPestana] = useState<'buscar' | 'recomendados' | 'ia'>('buscar');
  const [fraseRec, setFraseRec] = useState('');
  const [recomendados, setRecomendados] = useState<ConceptoParaOpcion[] | null>(null);
  const [recomendando, setRecomendando] = useState(false);
  const pedirRecomendados = async () => {
    if (!p.recomendar || fraseRec.trim().length < 2) return;
    setRecomendando(true);
    try {
      setRecomendados(await p.recomendar(fraseRec.trim()));
    } catch {
      setRecomendados([]);
    } finally {
      setRecomendando(false);
    }
  };
  const [texto, setTexto] = useState('');
  const [resultados, setResultados] = useState<ConceptoParaOpcion[] | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [elegido, setElegido] = useState<ConceptoParaOpcion | null>(null);
  const [descIa, setDescIa] = useState('');
  const [unidadIa, setUnidadIa] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (pestana !== 'buscar') return;
    const q = texto.trim();
    if (q.length < 2) {
      setResultados(null);
      return;
    }
    const t = setTimeout(() => {
      setBuscando(true);
      void p
        .buscarTexto(q)
        .catch(() => [] as ConceptoParaOpcion[])
        .then((r) => {
          setResultados(r);
          setBuscando(false);
        });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texto, pestana]);

  const concepto =
    pestana !== 'ia'
      ? elegido
        ? { origen: 'catalogo' as const, catalogId: elegido.catalogId, clave: elegido.clave, descripcion: elegido.descripcion, unidad: elegido.unidad, precio: elegido.precio }
        : null
      : descIa.trim().length >= 3 && unidadIa.trim()
        ? { origen: 'ia' as const, catalogId: null, clave: null, descripcion: descIa.trim(), unidad: unidadIa.trim(), precio: null }
        : null;
  // ¿Se cobra igual que el principal? Entonces la cantidad es su misma medición (y no hace falta escribirla).
  const hayMisma = !!concepto && !!p.principal.generador && p.principal.cantidad > 0 && mismaMedicion(p.principal, concepto.unidad) != null;

  const agregar = async () => {
    if (!concepto) return;
    setError(null);
    const r = renglonDeAdicional({ principal: p.principal, concepto, cantidadTexto: cantidad });
    if (!r.ok) {
      setError(r.motivo);
      return;
    }
    setGuardando(true);
    try {
      await p.onAgregar(r.renglon);
      p.onCerrar();
    } catch (e) {
      setError((e as Error).message || 'No se pudo agregar. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && !guardando && p.onCerrar()}>
      <DialogSurface style={{ width: 640, maxWidth: 'calc(100vw - 32px)' }}>
        <DialogBody>
          <DialogTitle>
            Agregar algo que falta
            <div style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-secondary)', marginTop: 2 }}>A: {corto(p.principal.descripcion, 90)}</div>
          </DialogTitle>
          <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <TabList selectedValue={pestana} onTabSelect={(_, d) => setPestana(d.value as typeof pestana)} size="small">
              <Tab value="buscar">Buscar en mis catálogos</Tab>
              {p.recomendar && <Tab value="recomendados">Recomendados</Tab>}
              <Tab value="ia">Generar con IA</Tab>
            </TabList>
            {pestana === 'buscar' ? (
              <>
                <Input
                  contentBefore={<Search16Regular />}
                  value={texto}
                  placeholder="Busca el concepto que falta: «cimbra», «impermeabilizante»…"
                  aria-label="Buscar el concepto que falta"
                  onChange={(_, d) => {
                    setTexto(d.value);
                    setElegido(null);
                  }}
                />
                {elegido ? (
                  <div style={estilos.elegido}>
                    Elegiste <b>{elegido.clave}</b> · {corto(elegido.descripcion)} — {unidadBonita(elegido.unidad) || 's/u'} ·{' '}
                    {elegido.precio != null ? p.formatoPrecio(elegido.precio) : 'sin precio'}{' '}
                    <button type="button" style={estilos.link} onClick={() => setElegido(null)}>
                      Elegir otro
                    </button>
                  </div>
                ) : (
                  <>
                    {buscando && <Spinner size="extra-tiny" label="Buscando…" labelPosition="after" />}
                    {resultados && resultados.length === 0 && !buscando && <div style={estilos.sub}>No encontré conceptos con esas palabras.</div>}
                    {resultados && resultados.length > 0 && (
                      <div role="listbox" aria-label="Conceptos" style={estilos.lista}>
                        {resultados.map((o) => (
                          <button key={o.resourceId} type="button" role="option" aria-selected={false} style={estilos.item} onClick={() => setElegido(o)}>
                            <b>{o.clave}</b> · {corto(o.descripcion)}
                            <span style={estilos.sub}>
                              {' '}
                              — {unidadBonita(o.unidad) || 's/u'} · {o.precio != null ? p.formatoPrecio(o.precio) : 'sin precio'}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            ) : pestana === 'recomendados' ? (
              <>
                <div style={estilos.sub}>Escribe qué falta y te muestro los conceptos de tus catálogos que mejor le corresponden. Pedirlos usa tokens la primera vez.</div>
                <Input
                  contentBefore={<Search16Regular />}
                  value={fraseRec}
                  placeholder="Qué falta: «cimbra», «impermeabilizante»…"
                  aria-label="Qué falta"
                  onChange={(_, d) => {
                    setFraseRec(d.value);
                    setElegido(null);
                  }}
                />
                {elegido ? (
                  <div style={estilos.elegido}>
                    Elegiste <b>{elegido.clave}</b> · {corto(elegido.descripcion)} — {unidadBonita(elegido.unidad) || 's/u'} ·{' '}
                    {elegido.precio != null ? p.formatoPrecio(elegido.precio) : 'sin precio'}{' '}
                    <button type="button" style={estilos.link} onClick={() => setElegido(null)}>
                      Elegir otro
                    </button>
                  </div>
                ) : (
                  <>
                    <span>
                      <Button size="small" appearance="primary" disabled={recomendando || fraseRec.trim().length < 2} onClick={() => void pedirRecomendados()}>
                        Ver recomendados
                      </Button>
                    </span>
                    {recomendando && <Spinner size="extra-tiny" label="Buscando los que mejor corresponden…" labelPosition="after" />}
                    {recomendados && recomendados.length === 0 && !recomendando && <div style={estilos.sub}>No encontré recomendados. Prueba con «Buscar en mis catálogos».</div>}
                    {recomendados && recomendados.length > 0 && (
                      <div role="listbox" aria-label="Recomendados" style={estilos.lista}>
                        {recomendados.map((o) => (
                          <button key={o.resourceId} type="button" role="option" aria-selected={false} style={estilos.item} onClick={() => setElegido(o)}>
                            <b>{o.clave}</b> · {corto(o.descripcion)}
                            <span style={estilos.sub}>
                              {' '}
                              — {unidadBonita(o.unidad) || 's/u'} · {o.precio != null ? p.formatoPrecio(o.precio) : 'sin precio'}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                <div style={estilos.sub}>
                  Descríbelo como lo dirías en obra. Entra sin precio y se genera con IA solo cuando tú lo pidas; no gasta nada ahora.
                </div>
                <Input value={descIa} placeholder="Qué trabajo es (p. ej. «Impermeabilizado de azotea»)" aria-label="Qué trabajo es" onChange={(_, d) => setDescIa(d.value)} />
                <Input value={unidadIa} placeholder="Unidad (m², m, pza…)" aria-label="Unidad" style={{ maxWidth: 200 }} onChange={(_, d) => setUnidadIa(d.value)} />
              </>
            )}
            {concepto && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 13, fontWeight: 600 }} htmlFor="adicional-cantidad">
                  Cantidad ({unidadBonita(concepto.unidad) || 'sin unidad'})
                </label>
                <Input
                  id="adicional-cantidad"
                  value={cantidad}
                  inputMode="decimal"
                  placeholder={hayMisma ? 'Déjalo vacío: usa la misma medición del concepto' : 'Escribe la cantidad'}
                  style={{ maxWidth: 260 }}
                  onChange={(_, d) => setCantidad(d.value)}
                />
                <div style={estilos.sub}>
                  {hayMisma
                    ? 'Se cobra en la misma unidad que el concepto: si lo dejas vacío, lleva su misma medición (y se puede ver de dónde sale).'
                    : 'Esta unidad no es la del concepto: escribe la cantidad. Quedará marcada como «puesta por ti».'}
                </div>
              </div>
            )}
            {error && <div style={estilos.aviso}>{error}</div>}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" disabled={guardando} onClick={p.onCerrar}>
              Cancelar
            </Button>
            <Button appearance="primary" disabled={!concepto || guardando} icon={guardando ? <Spinner size="extra-tiny" /> : undefined} onClick={() => void agregar()}>
              Agregar
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

const estilos = {
  sub: { fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 },
  link: { background: 'none', border: 'none', padding: 0, color: 'var(--accent)', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' },
  aviso: { fontSize: 13, color: 'var(--warn)', background: 'var(--warn-weak)', borderRadius: 6, padding: '6px 10px' },
  elegido: { fontSize: 12.5, background: 'var(--bg-raised)', borderRadius: 6, padding: '6px 10px' },
  lista: { maxHeight: 240, overflowY: 'auto' as const, border: '1px solid var(--border-subtle)', borderRadius: 6 },
  item: {
    display: 'block',
    width: '100%',
    textAlign: 'left' as const,
    padding: '5px 8px',
    border: 0,
    borderBottom: '1px solid var(--border-subtle)',
    background: 'var(--bg-surface)',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    fontSize: 12,
  },
};
